import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackFeatureUsage } from '../../utils/analytics';
import { getCoinIconUrl, DEFAULT_COIN_ICON } from '../../utils/helpers';
import './UnusualActivity.css';
import UnusualActivityModal from '../UnusualActivityModal/UnusualActivityModal';
import { SkeletonUnusualActivity } from '../SkeletonTradeRow/SkeletonTradeRow';

function UnusualActivity({
  unusualActivity,
  subscribeToUnusualActivity,
  unsubscribeFromUnusualActivity,
  changeUnusualActivityTimeframe,
  isConnected
}) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  // Subscribe to unusual activity when connected
  useEffect(() => {
    if (isConnected) {
      subscribeToUnusualActivity('1h');
    }

    return () => {
      if (isConnected) {
        unsubscribeFromUnusualActivity();
      }
    };
  }, [isConnected, subscribeToUnusualActivity, unsubscribeFromUnusualActivity]);

  const { top3, loading } = unusualActivity;

  // Get activity badge class
  const getActivityBadge = (heatLevel) => {
    if (heatLevel >= 4) return { label: t('unusualActivity.highActivity'), class: 'high' };
    if (heatLevel >= 2) return { label: t('unusualActivity.mediumActivity'), class: 'medium' };
    return { label: t('unusualActivity.lowActivity'), class: 'low' };
  };

  // Get item class based on heat level
  const getItemClass = (heatLevel) => {
    if (heatLevel >= 4) return 'hot';
    if (heatLevel >= 2) return 'warm';
    return '';
  };

  // Format activity change percentage
  const formatActivityChange = (change) => {
    return change > 0 ? `+${change}%` : `${change}%`;
  };


  return (
    <>
      <div className="card mb-3 unusual-activity-card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="card-title mb-0">
            <span className="ua-icon">&#129517;</span> {t('unusualActivity.title')}
            <div className="info">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#6c6c6c" width="20px">
                <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM11 15V17H13V15H11ZM13 13.3551C14.4457 12.9248 15.5 11.5855 15.5 10C15.5 8.067 13.933 6.5 12 6.5C10.302 6.5 8.88637 7.70919 8.56731 9.31346L10.5288 9.70577C10.6656 9.01823 11.2723 8.5 12 8.5C12.8284 8.5 13.5 9.17157 13.5 10C13.5 10.8284 12.8284 11.5 12 11.5C11.4477 11.5 11 11.9477 11 12.5V14H13V13.3551Z"></path>
              </svg>
              <div className="tooltip-card">
                <div className="card-body">
                  <h5>{t('unusualActivity.tooltipTitle')}</h5>
                  <span className="divider"></span>
                  <p>{t('unusualActivity.tooltipDesc')}</p>
                </div>
              </div>
            </div>
          </div>
          <button className="details-btn" onClick={() => {
            trackFeatureUsage('unusual_activity_modal', { page: '/trades' });
            setShowModal(true);
          }}>
            {t('common.seeMore')}
          </button>
        </div>

        <div className="card-body pd">
          <div className="unusual-activity-list">
            {loading ? (
              <SkeletonUnusualActivity count={3} />
            ) : top3.length === 0 ? (
              <div className="ua-empty">{t('unusualActivity.noActivity')}</div>
            ) : (
              top3.map((coin, index) => {
                const badge = getActivityBadge(coin.heatLevel);
                return (
                  <div
                    key={coin.symbol}
                    className={`unusual-coin-item ${getItemClass(coin.heatLevel)}`}
                  >
                    <div className="coin-info">
                      <img
                        src={getCoinIconUrl(coin.symbol)}
                        alt={coin.symbol}
                        title={coin.symbol}
                        onError={(e) => {
                          e.target.src = DEFAULT_COIN_ICON;
                        }}
                      />
                      <span className="coin-name">{coin.symbol}</span>
                    </div>
                    <div className="activity-indicator">
                      <span className={`activity-badge ${badge.class}`}>
                        {badge.label}
                      </span>
                      <span className="activity-change">
                        {formatActivityChange(coin.activityChange)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <UnusualActivityModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          unusualActivity={unusualActivity}
          changeTimeframe={changeUnusualActivityTimeframe}
        />
      )}
    </>
  );
}

export default UnusualActivity;
